'use client';

import { useCallback, useState } from 'react';
import { PhoneIcon } from '@heroicons/react/24/outline';
import { clsx } from 'clsx';
import { RecruiterCallSoftphoneModal } from './RecruiterCallSoftphoneModal';

interface CallCandidateButtonProps {
  candidateName: string;
  candidateUserId: number;
  jobSlug?: string | null;
  disabled?: boolean;
  className?: string;
}

/**
 * Entry-point button for the recruiter softphone. Drop this into the
 * candidate profile / applicant card / list row wherever a "Call" affordance
 * makes sense. On click, opens the softphone modal which handles the whole
 * Twilio.Device lifecycle. Idempotent: closing the modal fully tears down
 * the WebRTC session (see useTwilioDevice teardown).
 */
export const CallCandidateButton = ({
  candidateName,
  candidateUserId,
  jobSlug,
  disabled = false,
  className,
}: CallCandidateButtonProps) => {
  const [open, setOpen] = useState(false);

  const handleClick = useCallback(() => {
    if (disabled) return;
    setOpen(true);
  }, [disabled]);

  const handleClose = useCallback(() => setOpen(false), []);

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        disabled={disabled}
        aria-label={`Call ${candidateName}`}
        className={clsx(
          'inline-flex items-center gap-2 rounded-full border border-secondary bg-secondary/10 px-4 py-2 text-sm font-semibold text-secondary transition hover:bg-secondary/20',
          disabled && 'cursor-not-allowed opacity-50 hover:bg-secondary/10',
          className,
        )}
      >
        <PhoneIcon className="size-4" />
        Call candidate
      </button>

      {open && (
        <RecruiterCallSoftphoneModal
          open={open}
          candidateName={candidateName}
          candidateUserId={candidateUserId}
          jobSlug={jobSlug ?? null}
          onClose={handleClose}
        />
      )}
    </>
  );
};
